import { useAppSelector } from "@/redux/hooks";
import { useSuggestedQueries } from "@brandclub/common-ui";
import { TextBox } from "@brandclub/bc-widgets";
import React from "react";
import { usePageContext } from "../hooks/usePageContext";
import { ConciergeWelcomeEmbedded } from "./ConciergeWelcomeEmbedded";

interface ConciergeWelcomeHeaderProps {
  trendingSearches?: Array<{ text: string }>;
  onSearch: (searchTerm: string) => void;
  brandId?: number;
  isEmbedded?: boolean;
}

const ConciergeWelcomeHeader = ({
  trendingSearches,
  onSearch,
  brandId,
  isEmbedded = false,
}: ConciergeWelcomeHeaderProps) => {
  const { data, isLoading: isLoadingSuggestedQueries } =
    useSuggestedQueries(brandId);

  const userProfile = useAppSelector(({ userProfile }) => userProfile);
  const userName = userProfile?.given_name || "";

  // Memoize displayQueries to prevent recalculation on every render
  // Extract just the query strings from suggestedQuestions objects
  const displayQueries = React.useMemo(
    () =>
      data?.suggestedQuestions?.map((item) => item.query) ||
      trendingSearches?.map((search) => search.text) ||
      [],
    [data?.suggestedQuestions, trendingSearches]
  );

  // Get page context (always call hook, but only use when embedded)
  const pageContext = usePageContext();

  // Custom welcome screen for embedded mode
  if (isEmbedded) {
    return (
      <ConciergeWelcomeEmbedded
        displayQueries={displayQueries}
        isLoadingSuggestedQueries={isLoadingSuggestedQueries}
        onSearch={onSearch}
        pageContext={pageContext}
      />
    );
  }

  // Full welcome screen for non-embedded mode
  return (
    <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:w-full tw:text-center">
      {/* CONCIERGE label */}
      <TextBox
        widgetId="concierge-title-label"
        displayName="Concierge Label"
        initialState={{
          content: "CONCIERGE",
          fontSize: 16,
          fontWeight: "bold",
          color: "#031425",
          textAlign: "center",
          lineHeight: 2,
          letterSpacing: 2,
          textTransform: "uppercase",
        }}
      />

      {/* Personalized heading */}
      <div style={{ marginTop: "16px" }}>
        <TextBox
          widgetId="concierge-welcome-heading"
          displayName="Welcome Heading"
          initialState={{
            content: `Let us help you find what you've been looking for${userName ? `, ${userName}` : ""}.`,
            fontSize: 24,
            fontWeight: "semibold",
            color: "#031425",
            textAlign: "center",
            lineHeight: 1.33,
            letterSpacing: 0.08,
            textTransform: "none",
          }}
        />
      </div>
    </div>
  );
};

export default ConciergeWelcomeHeader;
